import { ArrowRightIcon, RefreshIcon } from "@heroicons/react/outline";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { postData } from "../backend";
import { MIMETYPE_ICON_MAP } from "../utils";

interface Message {
  role: "user" | "assistant";
  content: string;
}
interface Props {
  contentOffset: number;
}

const CLANKING_SPEED = 50;
const FILE_HEADER = "#file:";

function GenerateInterface(props: Props) {
  const { t } = useTranslation();

  const [state, setState] = useState<"initial" | "loading" | "clanking">("initial");

  const [token, setToken] = useState<string>();
  const [prompt, setPrompt] = useState("");
  const [response, setResponse] = useState("");

  const [showResetButton, setShowResetButton] = useState(false);

  const [conversation, setConversation] = useState<Array<Message>>([]);

  const scrollRef = useRef<HTMLDivElement>(null);

  const typeWordAndNext = useCallback((words: Array<string>, index: number, onComplete: () => void) => {
    if (index >= words.length)
      return onComplete();

    scrollRef.current!.scrollTop = scrollRef.current!.scrollHeight

    setResponse(r => r + " " + words[index]);
    setTimeout(() => typeWordAndNext(words, index + 1, onComplete), CLANKING_SPEED);
  }, []);

  const clankOutResponse = useCallback((text: string, onComplete: () => void) => {
    setResponse("");

    const words = text.split(" ");
    typeWordAndNext(words, 0, onComplete);

  }, [typeWordAndNext]);

  function handleReset() {
    setToken(undefined);
    setConversation([]);
    setShowResetButton(false);
  }

  function submitPrompt() {
    if (state !== "initial")
      return;

    setConversation(prev => [...prev, { role: "user", content: prompt }]);
    setTimeout(() => scrollRef.current!.scrollTop = scrollRef.current!.scrollHeight, 50);

    setState("loading");
    postData("/documentai/chat/", { prompt, token })
      .then(data => {
        const { token, lastMessage } = data;
        setToken(token);

        setState("clanking");
        if (lastMessage["content"].startsWith(FILE_HEADER)) {
          const feedbackMessage = { role: "assistant", content: "Here is a document I have generated, please let me know if you have any feedback on how I can improve." }
          clankOutResponse(feedbackMessage["content"], () => {
            setResponse("");
            setState("initial");

            setConversation(prev => [...prev, feedbackMessage, lastMessage]);
            setShowResetButton(true);

            setTimeout(() => scrollRef.current!.scrollTop = scrollRef.current!.scrollHeight, 50);
          });
        } else {
          clankOutResponse(lastMessage["content"], () => { setState("initial"); setResponse(""); setConversation(prev => [...prev, lastMessage]) })
        }
      })
      .catch(() => setState("initial"));

    setPrompt("");
  }

  return (
    <div className="border border-gray-200 rounded-lg bg-white w-full h-full">
      {conversation.length === 0 &&
        <div className="flex flex-col justify-center items-center gap-8 h-full">
          <div className="flex flex-col items-center">
            <div className="font-gilroy font-semibold text-gray-600 text-3xl">{t("What would you like to create?")}</div>
          </div>
          <div className="relative">
            <textarea className="border rounded w-[800px] resize-none p-4 min-h-[100px]" placeholder={t("Describe the type of document you would like to create...")} value={prompt} onChange={e => { setPrompt(e.currentTarget.value) }} onKeyDown={(e => { if (e.key === "Enter") { e.preventDefault(); submitPrompt() } })} />
            <button className="absolute bg-blue right-2 bottom-4 text-white py-2 px-3 rounded" onClick={submitPrompt}><ArrowRightIcon className="w-4 h-4" /></button>
          </div>
        </div>
      }

      {conversation.length >= 1 &&
        <div className="flex flex-col w-[800px] mx-auto h-full">
          <div className="flex flex-col overflow-y-scroll gap-8 py-10 scrollbar-hidden" ref={scrollRef} style={{ height: `calc(100vh - ${props.contentOffset}px - 32px - 116px)` }}>
            {conversation.map((message, index) => {
              if (message.content.startsWith(FILE_HEADER)) {
                const fileJson = JSON.parse(message.content.substring(FILE_HEADER.length));

                return (
                  <a key={index} href={fileJson["url"]} target="_blank" rel="noreferrer" className="flex flex-col relative h-40 w-48 bg-gray-100 border shadow overflow-hidden rounded flex-shrink-0">
                    <img className="w-20 h-20 flex-shrink-0 m-auto" src={MIMETYPE_ICON_MAP[fileJson["mime_type"]]} alt="File Icon" />
                    <div className="bg-white p-2 font-semibold text-gray-600 text-sm text-center border-t truncate">{fileJson["name"]}</div>
                  </a>
                )
              }

              return (
                <div key={index} className={message.role === "user" ? "bg-gray-300 px-6 py-2 ml-auto max-w-[600px] font-medium flex-shrink-0 rounded-3xl" : "max-w-[600px] font-medium min-h-5 flex-shrink-0"}>
                  {message.content}
                </div>
              );
            })}
            {state === "loading" &&
              <div className="w-5 h-5 bg-blue rounded-full animate-pulse flex-shrink-0" />
            }
            {state === "clanking" &&
              <div className={"max-w-[600px] font-medium min-h-5"}>
                {response}
              </div>
            }
          </div>
          <div className="relative mt-auto flex-shrink-0 h-[100px] mb-4 w-full">
            {showResetButton &&
              <div className="w-full absolute -top-12">
                <button className="px-4 py-1 bg-white border rounded-full flex items-center gap-2 text-gray-400 hover:text-gray-600 font-semibold mx-auto shadow" onClick={handleReset}>
                  <RefreshIcon className="w-5 h-5" /> Start again
                </button>
              </div>
            }
            <textarea className="border rounded w-[800px] resize-none p-4 h-full" placeholder={t("Describe the type of document you would like to create...")} value={prompt} onChange={e => { setPrompt(e.currentTarget.value) }} onKeyDown={(e => { if (e.key === "Enter") { e.preventDefault(); submitPrompt() } })} />
            <button className="absolute bg-blue right-2 bottom-2 text-white py-2 px-3 rounded" onClick={submitPrompt}><ArrowRightIcon className="w-4 h-4" /></button>
          </div>
        </div>
      }
    </div>
  );
}

export default GenerateInterface;
